// utils/statusConfig.ts
import { PostStatus } from "@/core/types/postTypes";

export interface StatusConfig {
  // Base properties
  label: string;
  icon: string;

  // Color variants
  colors: {
    primary: string; // Main text color
    secondary: string; // Secondary text color
    background: string; // Background color
    hover: string; // Hover background color
    dot: string; // Status dot color
    border: string; // Border color (if needed)
  };

  // Semantic meaning
  category: "draft" | "processing" | "ready" | "published" | "archived";
  order: number; // For sorting
}

export const STATUS_CONFIGS: Record<PostStatus, StatusConfig> = {
  ai_generated: {
    label: "Generato AI",
    icon: "🤖",
    colors: {
      primary: "text-violet-700",
      secondary: "text-violet-600",
      background: "bg-violet-100",
      hover: "bg-violet-200",
      dot: "bg-violet-400",
      border: "border-violet-200",
    },
    category: "draft",
    order: 0,
  },

  draft: {
    label: "Bozza",
    icon: "📝",
    colors: {
      primary: "text-slate-700",
      secondary: "text-slate-600",
      background: "bg-slate-100",
      hover: "bg-slate-200",
      dot: "bg-slate-400",
      border: "border-slate-200",
    },
    category: "draft",
    order: 1,
  },

  caption_generated: {
    label: "Didascalia generata",
    icon: "✨",
    colors: {
      primary: "text-blue-800",
      secondary: "text-blue-600",
      background: "bg-blue-100",
      hover: "bg-blue-200",
      dot: "bg-blue-500",
      border: "border-blue-200",
    },
    category: "ready",
    order: 2,
  },

  approval_pending: {
    label: "In attesa",
    icon: "⏳",
    colors: {
      primary: "text-amber-800",
      secondary: "text-amber-600",
      background: "bg-amber-100",
      hover: "bg-amber-200",
      dot: "bg-amber-500",
      border: "border-amber-200",
    },
    category: "processing",
    order: 3,
  },

  ready: {
    label: "Pronto",
    icon: "✅",
    colors: {
      primary: "text-teal-800",
      secondary: "text-teal-600",
      background: "bg-teal-100",
      hover: "bg-teal-200",
      dot: "bg-teal-500",
      border: "border-teal-200",
    },
    category: "ready",
    order: 5,
  },

  scheduled: {
    label: "Programmato",
    icon: "⏰",
    colors: {
      primary: "text-purple-800",
      secondary: "text-purple-600",
      background: "bg-purple-100",
      hover: "bg-purple-200",
      dot: "bg-purple-500",
      border: "border-purple-200",
    },
    category: "ready",
    order: 6,
  },

  partially_published: {
    label: "Pubblicato parzialmente",
    icon: "⚠️",
    colors: {
      primary: "text-amber-800",
      secondary: "text-amber-600",
      background: "bg-amber-100",
      hover: "bg-amber-200",
      dot: "bg-amber-500",
      border: "border-amber-200",
    },
    category: "published",
    order: 7,
  },

  published: {
    label: "Pubblicato",
    icon: "🎉",
    colors: {
      primary: "text-emerald-800",
      secondary: "text-emerald-600",
      background: "bg-emerald-100",
      hover: "bg-emerald-200",
      dot: "bg-emerald-500",
      border: "border-emerald-200",
    },
    category: "published",
    order: 8,
  },

  archived: {
    label: "Archiviato",
    icon: "🗑️",
    colors: {
      primary: "text-gray-600",
      secondary: "text-gray-500",
      background: "bg-gray-100",
      hover: "bg-gray-200",
      dot: "bg-gray-400",
      border: "border-gray-200",
    },
    category: "archived",
    order: 9,
  },
};

// ... rest of the utility functions remain the same

// Utility functions
export const getStatusConfig = (status: PostStatus): StatusConfig => {
  return STATUS_CONFIGS[status] || STATUS_CONFIGS["draft"];
};

export const getStatusesByCategory = (
  category: StatusConfig["category"],
): PostStatus[] => {
  return Object.entries(STATUS_CONFIGS)
    .filter(([_, config]) => config.category === category)
    .map(([status]) => status as PostStatus);
};

export const getSortedStatuses = (): PostStatus[] => {
  return Object.entries(STATUS_CONFIGS)
    .sort(([, a], [, b]) => a.order - b.order)
    .map(([status]) => status as PostStatus);
};

// Style generators for different use cases
export const getDropdownStyles = (status: PostStatus) => {
  const config = getStatusConfig(status);
  return {
    bg: `${config.colors.background} ${config.colors.hover}`,
    text: config.colors.primary,
    dot: config.colors.dot,
    icon: config.icon,
  };
};

export const getStatisticsStyles = (status: PostStatus) => {
  const config = getStatusConfig(status);
  return {
    color: config.colors.secondary,
    bgColor: config.colors.background,
    icon: config.icon,
  };
};

export const getCardStyles = (status: PostStatus) => {
  const config = getStatusConfig(status);
  return {
    textColor: config.colors.primary,
    backgroundColor: config.colors.background,
    borderColor: config.colors.border,
    dotColor: config.colors.dot,
    icon: config.icon,
  };
};
